import { isAndroid, isIOS, isMobile } from 'react-device-detect'
import React, { Fragment, ReactNode, useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import Reparent from './Reparent'
import FOVCamera from '../FOVCamera'
import PanHandler from './PanHandlerComponent'
import MotionControls from './MotionControls'
import { CameraInputMethods, useAppStore } from '../../store/AppStore'
import { ForwardedModel } from '../../types/GLTFAction'
import { useMoveStore } from '../../store/moveStore'
import GenericPopup from '../Generic/GenericPopup'
import { Html } from '@react-three/drei/web/Html'

export type ControlWrapperProps = {
  cameraTrack?: THREE.Object3D | null
  children: ReactNode
}

export default function ControlWrapper ({ children, ...props }: ControlWrapperProps) {
  const inputMethod = useAppStore((state) => state.inputMethod)
  const updateInputMethod = useAppStore((state) => state.updateInputMethod)
  const movementIsLocked = useMoveStore((state) => state.movementIsLocked)
  const [gyroPrompt, setGyroPrompt] = useState(false)

  const [child, childRef] = React.useState(null)
  const [cameraTrack, setCamera] = React.useState(props.cameraTrack)

  const cameraProps = {
    up: new THREE.Vector3(0, 2, 0),
    near: 0.0001,
    far: 10000,
    scale: 27,
    makeDefault: true
  }

  const camera = useRef<THREE.Camera>(null)
  const refBlankCameraParent = useRef<THREE.Object3D | null>(null)
  const refDragCameraParent = useRef<THREE.Object3D | null>(null)
  const refGyroCameraParent = useRef<THREE.Object3D | null>(null)
  const refDragNGyroCameraParent = useRef<THREE.Object3D | null>(null)
  const dragSensitivityOverride = 1.4
  const [currentCameraParent, setCurrentCameraParent] = useState<THREE.Object3D>(new THREE.Object3D())

  useEffect(() => {
    setCurrentCameraParent(refBlankCameraParent.current as THREE.Object3D)
  }, [])

  useEffect(() => {
    setTimeout(() => {
      // Workaround for three bug
      document.querySelector('#mainCanvas > div')?.removeAttribute('style')
      document.querySelector('#mainCanvas > div > div')?.removeAttribute('style')
    }, 100)
  }, [gyroPrompt])

  const requestIOSPermission = () => {
    let newState = 'None'
    ;(DeviceOrientationEvent as any).requestPermission()
      .then((response: string) => {
        if (response === 'granted') {
          newState = 'Gyro'
        } else {
          newState = 'Drag'
        }
      })
      .catch((error: Error) => {
        if (error.name === 'NotAllowedError') {
          setGyroPrompt(true)
        }
      })
      .finally(() => {
        updateInputMethod((newState as CameraInputMethods))
        if (newState !== 'None') {
          useAppStore.getState().showRoom()
          setGyroPrompt(false)
        }
      })
  }

  const handlePermissionRejected = () => {
    updateInputMethod(('Drag' as CameraInputMethods))
    useAppStore.getState().showRoom()
    setGyroPrompt(false)
  }

  useEffect(() => {
    if (inputMethod === 'None') {
      let newState = 'None'
      if (isMobile) {
        // Enable?
        if (isAndroid) {
          // For android - we don't need to ask permission????
          newState = 'Gyro'
        } else if (isIOS) {
          // But we do need to ask permission for iOS
          requestIOSPermission()
        }
      } else {
        newState = 'Drag'
      }

      updateInputMethod((newState as CameraInputMethods))
    }
  }, [refGyroCameraParent.current, refDragCameraParent.current])

  useEffect(() => {
    if (inputMethod === 'Gyro') setCurrentCameraParent(refGyroCameraParent.current as THREE.Object3D)
    else if (inputMethod === 'Drag') setCurrentCameraParent(refDragCameraParent.current as THREE.Object3D)
    else if (inputMethod === 'Both') setCurrentCameraParent(refDragNGyroCameraParent.current as THREE.Object3D)

    if (inputMethod !== 'None') useAppStore.getState().showRoom()
  }, [refGyroCameraParent.current, refDragCameraParent.current, inputMethod])

  const handleChild = () => {
    if (children && React.isValidElement(children)) {
      return React.Children.map(children, function (child) {
        return React.cloneElement(child, { ref: childRef })
      })
    }
    return null
  }

  useEffect(() => {
    let handleAnimation = true
    if (child && !cameraTrack) {
      const c = child as JSX.IntrinsicElements['group']
      let cameraSet = !!cameraTrack
      if (c.traverse) {
        let cameraObject: THREE.Object3D | null = null
        c.traverse((node: THREE.Object3D) => {
          if (['CAM', 'cameraLook_1'].includes(node.name) && !cameraSet) {
            setCamera(node)
            cameraSet = true
          } else if (node.type === 'PerspectiveCamera' && !cameraSet && !cameraObject) {
            cameraObject = node
          }
        })

        if (!cameraSet && cameraObject) {
          // We couldn't find the required camera object (the one named "CAM"), but we found other cameras
          // We will fall back to the first one found
          setCamera(cameraObject)
          // eslint-disable-next-line no-console
          console.warn('Setting fallback camera', cameraObject)
        }
      }

      if (typeof (c as ForwardedModel).playAnimation === 'undefined') {
        handleAnimation = false
      }
    }

    if (child) {
      // Listen for animation state change and trigger next animation
      useMoveStore.subscribe((newValue, oldValue) => {
        if (!newValue.movementIsLocked && oldValue.animationStep !== newValue.animationStep && handleAnimation) {
          const animation = 'move' + newValue.animationStep
          const c = (child as ForwardedModel)
          c.playAnimation(animation)
        }
      })
    }
  }, [child])

  return (
    <Fragment>
      {gyroPrompt &&
        <Html prepend
              wrapperClass={'fixed inset-0 w-full h-full flex items-center justify-center z-100'}
              calculatePosition={() => {
                return []
              }}>
          <GenericPopup title={'Enable Gyro Controls?'}
                        buttonText={'Yes'}
                        callback={requestIOSPermission}
                        callbackDelay={1}
                        extraButtons={[{ text: 'No', callback: handlePermissionRejected }]} />
        </Html>
      }
      <Reparent targetParent={cameraTrack}>
        <Reparent targetParent={currentCameraParent as THREE.Object3D}>
          <FOVCamera ref={camera} cameraProps={cameraProps} holderProps={{}} />
        </Reparent>
        <object3D ref={refBlankCameraParent} />
        <group>
          {inputMethod === 'Drag' &&
            <PanHandler dragSensitivity={dragSensitivityOverride} enabled={!movementIsLocked}>
              <object3D ref={refDragCameraParent} />
            </PanHandler>
          }

          {inputMethod === 'Gyro' &&
            <MotionControls>
              <object3D ref={refGyroCameraParent} />
            </MotionControls>
          }

          {inputMethod === 'Both' &&
            <PanHandler dragSensitivity={dragSensitivityOverride} enabled={!movementIsLocked}>
              <MotionControls>
                <object3D ref={refDragNGyroCameraParent} />
              </MotionControls>
            </PanHandler>
          }
        </group>
      </Reparent>
      {handleChild()}
    </Fragment>
  )
}
